<template>
  <div class="c-summary-by-project">
    <v-card>
      <v-card-title>
        {{ cardTitle }}
        <v-spacer></v-spacer>
        <v-btn
          color="success"
          small
          @click.prevent="openSummaryDialog(false)"
          :disabled="Array.isArray(projectId)"
        >
          New summary
        </v-btn>
      </v-card-title>
      <div class="d-flex align-center flex-wrap pa-4" style="gap: 1rem;">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search table"
          hide-details
          dense
          outlined
          class="flex-grow-1"
          style="min-width: 250px; max-width: 400px;"
        ></v-text-field>
        <v-menu v-model="dateFromMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="dateFrom"
              label="From Date"
              prepend-icon="mdi-calendar"
              readonly
              hide-details
              dense
              outlined
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker v-model="dateFrom" @input="dateFromMenu = false"></v-date-picker>
        </v-menu>
        <v-menu v-model="dateToMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="dateTo"
              label="To Date"
              prepend-icon="mdi-calendar"
              readonly
              hide-details
              dense
              outlined
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker v-model="dateTo" @input="dateToMenu = false"></v-date-picker>
        </v-menu>
        <v-btn color="primary" @click="getSummary" class="ml-2">Filter</v-btn>
        <v-btn icon @click="clearFilters" title="Clear filters">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <v-divider></v-divider>
      <v-card-text class="pt-0">
        <v-data-table
          dense
          fixed-header
          mobile-breakpoint="0"
          height="70vh"
          :key="'summary-list-' + updateDialog"
          :headers="summaryHeaders"
          :items="summary"
          :search="search"
          :item-class="setActive"
          :group-by="Array.isArray(projectId) ? 'Project_Name' : null"
          :items-per-page="-1"
          hide-default-footer
        >
          <template v-slot:group.header="{ group, isOpen, toggle, items }">
            <td :colspan="summaryHeaders.length" class="py-1" style="cursor: pointer;" @click="toggle">
              <div class="d-flex align-center" style="gap: 0.75rem;">
                <v-btn @click.stop="toggle" x-small icon>
                  <v-icon>{{ isOpen ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
                </v-btn>
                <span class="font-weight-bold text-h6 primary--text">{{ group }}</span>
                <v-chip small color="primary" outlined>{{ items.length }} summaries</v-chip>
              </div>
            </td>
          </template>
          <template v-slot:[`item.Details`]="{ item }">
            <div class="d-flex align-center">
              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" small icon @click="getSummaryById(item)">
                    <v-icon color="primary">mdi-alpha-r-circle</v-icon>
                  </v-btn>
                </template>
                <span>Reporting</span>
              </v-tooltip>
              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" small icon @click="openSummaryDialog(item)">
                    <v-icon color="primary">mdi-pencil-circle</v-icon>
                  </v-btn>
                </template>
                <span>Edit</span>
              </v-tooltip>
              <v-tooltip bottom color="red darken-2">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" small icon @click="currentSummaryFromDate = item.From_Date; currentSummaryToDate = item.To_Date; deleteSummaryId = item.Summary_ID; deleteDialog = true">
                    <v-icon color="red darken-2">mdi-delete-circle</v-icon>
                  </v-btn>
                </template>
                <span>Delete</span>
              </v-tooltip>
              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" small text color="primary" @click="changeStatus(item)">
                    {{ item.Status === 'C' ? 'Closed' : 'Opened' }}
                  </v-btn>
                </template>
                <span>Click to {{ item.Status === 'C' ? 'open' : 'close' }}</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <SummaryDetails
      :key="'update-reports-' + showDetails"
      :list="summaryDetails"
      :isClosedSummary="isClosedSummary"
      v-model="showDetails"
    />
    <SummaryDialog
      :key="(editSummaryItem ? editSummaryItem.Status + '-' + editSummaryItem.Summary_ID : updateDialog)"
      :editData="editSummaryItem"
      v-model="showSummaryDialog"
      @onSave="saveSummary"
    />
    <!-- Remove dialog -->
    <ConfirmOrDeniedDialog
      v-if="deleteDialog"
      v-model="deleteDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="`You can't restore the Summary for [${currentSummaryFromDate}, ${currentSummaryToDate}] after delete`"
      @onDeleteClick="deleteSummary"
      @onCancelClick="closeDeleteSummaryDialog"
      @onCloseClick="closeDeleteSummaryDialog"
    />
    <!-- /Remove dialog -->
  </div>
</template>
<script>
export default {
  name: 'SummaryByProject',
  components: {
    SummaryDetails: () => import('@/components/summary/SummaryDetails.vue'),
    SummaryDialog: () => import('@/components/summary/SummaryDialog.vue'),
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    projectId: {
      required: true,
      type: [String, Number, Array]
    }
  },
  data () {
    return {
      summary: [],
      summaryDetails: [],
      updateDialog: 0,
      showDetails: false,
      showSummaryDialog: false,
      editSummaryItem: null,
      deleteDialog: false,
      deleteSummaryId: null,
      isClosedSummary: false,
      currentSummaryFromDate: '',
      currentSummaryToDate: '',
      dateFrom: null,
      dateTo: null,
      dateFromMenu: false,
      dateToMenu: false,
      search: '',
      selectedRow: null
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    cardTitle () {
      if (!this.summary.length) {
        return 'No summary'
      }

      if (Array.isArray(this.projectId)) {
        const projectNames = [...new Set(this.summary.map(item => item.Project_Name))]
        return `Summary of ${projectNames.join(', ')}`
      } else {
        return `Summary of ${this.summary[0].Project_Name}`
      }
    },
    summaryHeaders () {
      const headers = [
        {
          text: 'From date',
          value: 'From_Date',
          divider: true,
          cellClass: 'text-no-wrap',
          class: 'text-no-wrap'
        },
        {
          text: 'To date',
          value: 'To_Date',
          divider: true,
          cellClass: 'text-no-wrap',
          class: 'text-no-wrap'
        },
        {
          text: 'Summary',
          value: 'Summary',
          divider: true
        },
        {
          sortable: false,
          align: 'center',
          text: '#',
          value: 'Details',
          width: 100
        }
      ]
      if (Array.isArray(this.projectId)) {
        headers.unshift({
          text: 'Project',
          value: 'Project_Name',
          divider: true,
          class: 'text-no-wrap'
        })
      }
      return headers
    }
  },
  mounted () {
    if (this.projectId) {
      this.getSummary()
    }
  },
  methods: {
    getSummary () {
      const idParam = Array.isArray(this.projectId) ? this.projectId.join(',') : this.projectId
      if (!idParam) return

      const params = {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo
      }
      this.axios.get(`/api/summarybyproject/${idParam}`, {
        params,
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        res.data.forEach(item => {
          item.From_Date = new Date(item.From_Date).toISOString().substr(0, 10)
          item.To_Date = new Date(item.To_Date).toISOString().substr(0, 10)
        })
        this.summary = res.data
        this.updateDialog++
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + (error.response ? error.response.data : '') })
      })
    },
    clearFilters () {
      this.search = ''
      this.dateFrom = null
      this.dateTo = null
      this.getSummary()
    },
    closeDeleteSummaryDialog () {
      this.deleteSummaryId = null
      this.currentSummaryFromDate = ''
      this.currentSummaryToDate = ''
      this.deleteDialog = false
    },
    deleteSummary () {
      if (this.deleteSummaryId) {
        this.axios.delete(`/api/summary/${this.deleteSummaryId}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(() => {
          this.getSummary()
          this.$store.dispatch('setSuccess', { successMsg: 'Summary successfully deleted' })
        }).catch(error => {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        }).finally(() => {
          this.deleteDialog = false
          this.deleteSummaryId = null
        })
      }
    },
    saveSummary (e) {
      if (e.update) {
        this.updateSummary(e.value)
      } else {
        this.newSummary(e.value)
      }
    },
    newSummary (data) {
      this.axios.post('/api/summary', data, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(() => {
        this.getSummary()
        this.$store.dispatch('setSuccess', { successMsg: 'Summary successfully created' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    updateSummary (data) {
      this.axios.put(`/api/summary/${data.Summary_ID}`, data, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(() => {
        this.getSummary()
        this.$store.dispatch('setSuccess', { successMsg: 'Summary successfully updated' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    openSummaryDialog (item) {
      this.editSummaryItem = null
      if (item) {
        this.editSummaryItem = { ...item }
      }
      this.showSummaryDialog = true
    },
    setActive (item) {
      return this.selectedRow === item.Summary_ID ? 'grey lighten-3' : ''
    },
    getSummaryById (item) {
      this.selectedRow = item.Summary_ID
      this.isClosedSummary = item.Status === 'C'
      this.axios.get(`/api/repbysummary/${item.Summary_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.summaryDetails = res.data
        this.showDetails = true
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    changeStatus (item) {
      const apiVar = item.Status === 'C' ? 'uncloseweek' : 'closeweek'
      this.axios.get(`/api/${apiVar}/${item.Project_ID}/${item.From_Date}/${item.To_Date}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        const status = res.data[0]
        if (status === 0) {
          item.Status = apiVar === 'closeweek' ? 'C' : 'U'
          this.updateDialog++
          this.$store.dispatch('setSuccess', { successMsg: 'Summary successfully updated' })
        } else if (status === 2) {
          this.updateDialog++
          this.$store.dispatch('setError', { errorMsg: 'Add description to all reports' })
        }
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    }
  }
}
</script>
